const BASE = '/api/v1';

/**
 * @param {string} method - HTTP method
 * @param {string} path  - URL path (relative to BASE)
 * @param {*}      [body] - optional JSON-serializable request body
 */
async function request(method, path, body) {
  const token = localStorage.getItem('scb_auth_token');
  const headers = {};
  if (body) headers['Content-Type'] = 'application/json';
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(`${BASE}${path}`, {
    method,
    headers,
    credentials: 'include',
    body: body ? JSON.stringify(body) : undefined,
  });

  let bodyData = null;
  const contentType = res.headers.get('content-type') || '';
  if (res.status !== 204 && contentType.includes('json')) {
    try {
      bodyData = await res.json();
    } catch {
      // Non-JSON body — keep generic message.
    }
  }

  const { logApi } = await import('./store/debugStore.js');
  logApi({ method, url: `${BASE}${path}`, status: res.status, ok: res.ok, data: bodyData });

  if (!res.ok) {
    let detail = `Request failed with status ${res.status}`;
    if (bodyData) {
      detail = bodyData.message || bodyData.error || detail;
    }
    throw new ApiError(res.status, detail);
  }

  return bodyData ?? null;
}

/**
 * Standard error class carrying the HTTP status, used by callers to
 * distinguish network failures from API rejections.
 */
export class ApiError extends Error {
  constructor(status, message) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

/** Backend room DTOs use snake_case; this normalizes to the camelCase Room
 * typedef so page components don't need to guess field names. */
function normalizeRoom(raw) {
  if (!raw || typeof raw !== 'object') return raw;
  return {
    id: raw.id ?? '',
    guid: raw.guid ?? '',
    status: raw.status ?? 'waiting',
    contactId: raw.contact_id ?? undefined,
    operatorId: raw.operator_id ?? undefined,
    clientName: raw.client_name ?? undefined,
    maxClients: typeof raw.max_clients === 'number' ? raw.max_clients : 1,
    clientCount: typeof raw.client_count === 'number' ? raw.client_count : 0,
    recordingUrl: raw.recording_url ?? undefined,
    recordingStatus: raw.recording_status ?? undefined,
    durationSeconds: typeof raw.duration_seconds === 'number' ? raw.duration_seconds : undefined,
    createdAt: raw.created_at ?? '',
    startedAt: raw.started_at ?? undefined,
    endedAt: raw.ended_at ?? undefined,
    expiresAt: raw.expires_at ?? '',
  };
}

/** Backend recording DTOs use snake_case; this normalizes to camelCase */
function normalizeRecording(raw) {
  if (!raw || typeof raw !== 'object') return raw;
  return {
    id: raw.id ?? '',
    roomId: raw.room_id ?? raw.roomId ?? '',
    s3Key: raw.s3_key ?? raw.s3Key ?? '',
    s3Url: raw.s3_url ?? raw.s3Url ?? '',
    sizeBytes: typeof raw.size_bytes === 'number' ? raw.size_bytes : (typeof raw.sizeBytes === 'number' ? raw.sizeBytes : 0),
    durationSeconds: typeof raw.duration_seconds === 'number' ? raw.duration_seconds : (typeof raw.durationSeconds === 'number' ? raw.durationSeconds : 0),
    format: raw.format ?? 'mp4',
    status: raw.status ?? 'ready',
    errorMessage: raw.error_message ?? raw.errorMessage ?? undefined,
    createdAt: raw.created_at ?? raw.createdAt ?? '',
    processedAt: raw.processed_at ?? raw.processedAt ?? undefined,
  };
}

/**
 * @typedef {object} Room
 * @property {string} id
 * @property {string} guid - Unique public link for client join
 * @property {'waiting'|'active'|'ended'|'expired'} status
 * @property {string} [contactId]
 * @property {string} [operatorId]
 * @property {string} [clientName]
 * @property {number} maxClients - Default: 1
 * @property {number} clientCount - Current connected clients
 * @property {string} [recordingUrl]
 * @property {string} [recordingStatus] - pending, ready, error
 * @property {number} [durationSeconds]
 * @property {string} createdAt
 * @property {string} [startedAt]
 * @property {string} [endedAt]
 * @property {string} expiresAt
 */

/**
 * @typedef {object} Recording
 * @property {string} id
 * @property {string} roomId
 * @property {string} s3Key
 * @property {string} s3Url - Pre-signed download URL
 * @property {number} sizeBytes
 * @property {number} durationSeconds
 * @property {string} format - mp4, webm
 * @property {string} status - processing, ready, error
 * @property {string} [errorMessage]
 * @property {string} createdAt
 * @property {string} [processedAt]
 */

export const api = {
  /** @returns {Promise<{message: string}>} */
  health: () => request('GET', '/health'),

  /** @returns {Promise<{rooms: Room[]; total: number}>} */
  listRooms: (params) => {
    const qs = new URLSearchParams();
    if (params?.contactId) qs.set('contact_id', params.contactId);
    const suffix = qs.toString() ? `?${qs}` : '';
    return request('GET', `/rooms${suffix}`).then((d) => ({
      rooms: (Array.isArray(d?.rooms) ? d.rooms : []).map(normalizeRoom),
      total: typeof d?.total === 'number' ? d.total : 0,
    }));
  },

  /** @returns {Promise<Room>} */
  getRoom: (id) => request('GET', `/rooms/${id}`).then((d) => normalizeRoom(d)),

  /**
   * @param {{contact_id?: string; operator_id?: string; max_clients?: number; callback_url?: string}} body
   * @returns {Promise<Room>}
   */
  createRoom: (body) => request('POST', '/rooms', body),

  /** @returns {Promise<void>} */
  deleteRoom: (id) => request('DELETE', `/rooms/${id}`),

  /** @returns {Promise<Room>} — starts recording too */
  startCall: (id) => request('POST', `/rooms/${id}/start`, {}),

  /** @returns {Promise<Room>} — stops recording too */
  endCall: (id) => request('POST', `/rooms/${id}/end`, {}),

  /**
   * @param {string} id
   * @param {{sessionId?: string}} opts
   * @returns {Promise<{room: Room; ws_url: string}>}
   */
  operatorJoin: (id, opts) =>
    request('POST', `/rooms/${id}/operator/join`, opts ? { session_id: opts.sessionId || '' } : {}),

  /**
   * @param {string} id
   * @param {{name?: string; sessionId?: string}} opts
   * @returns {Promise<{room: Room; ws_url: string}>}
   */
  clientJoin: (id, opts) =>
    request('POST', `/rooms/${id}/client/join`, opts ? { name: opts.name || '', session_id: opts.sessionId || '' } : {}),

  /** Poll until the room status changes to something other than "waiting". */
  waitForCallToStart: async (id, onStatus, timeoutMs = 120_000) => {
    const deadline = Date.now() + timeoutMs;
    while (Date.now() < deadline) {
      const room = await api.getRoom(id);
      if (onStatus) onStatus(room);
      if (room.status !== 'waiting') return room;
      await new Promise((r) => setTimeout(r, 2000));
    }
    throw new ApiError(408, 'Timed out waiting for the call to start');
  },

  /** @returns {Promise<Room>} */
  startRecording: (id) => request('POST', `/rooms/${id}/record/start`),

  /** @returns {Promise<Room>} */
  stopRecording: (id) => request('POST', `/rooms/${id}/record/stop`),

  /** @returns {Promise<{message: string}>} */
  cleanupExpired: () => request('POST', '/admin/cleanup/expired'),

  /** @returns {Promise<Recording[]>} */
  listRecordings: () =>
    request('GET', '/recordings').then((d) =>
      (Array.isArray(d?.recordings) ? d.recordings : []).map(normalizeRecording)
    ),

  /**
   * @param {string} roomId
   * @param {Blob} blob
   * @param {string} [filename]
   * @returns {Promise<Recording>}
   */
  uploadRecording: async (roomId, blob, filename = 'recording.webm') => {
    const formData = new FormData();
    formData.append('video', blob, filename);
    const token = localStorage.getItem('scb_auth_token');
    const headers = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const res = await fetch(`${BASE}/rooms/${roomId}/record/upload`, {
      method: 'POST',
      credentials: 'include',
      headers,
      body: formData,
    });
    let bodyData = null;
    try {
      bodyData = await res.json();
    } catch {}
    if (!res.ok) {
      const detail = bodyData?.message || bodyData?.error || `Upload failed with status ${res.status}`;
      throw new ApiError(res.status, detail);
    }
    const rec = bodyData?.recording || bodyData;
    return normalizeRecording(rec);
  },

  /** @returns {Promise<void>} */
  deleteRecording: (id) => request('DELETE', `/recordings/${id}`),

  /** Auth endpoints */
  login: (login, password) => request('POST', '/auth/login', { login, password }),
  checkAuth: () => request('GET', '/auth/check'),
  logout: () => request('POST', '/auth/logout'),
};